import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import FrontendUpdateModal from "../../Chat/Components/FrontendUpdateModal"

type AppSocketMessage = {
    msg_type: string
    data?: any
}

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

const FRONTEND_VERSION =
    process.env.REACT_APP_FRONTEND_VERSION || process.env.REACT_APP_BUILD_VERSION || process.env.REACT_APP_COMMIT_SHA || "unknown"

const VERSION_STATUS_MESSAGE_TYPES = new Set([
    "frontend.version.status",
    "frontend.version.outdated",
    "app.version.status",
    "app.version.outdated",
    "version.status",
    "version.outdated",
])

type AppSocketListener = (message: AppSocketMessage) => void

type AppSocketContextType = {
    isConnected: boolean
    sendJson: (payload: unknown) => boolean
    addMessageListener: (listener: AppSocketListener) => () => void
}

type DebugState = {
    reconnectAttempts: number
    lastEvent: string
    lastMessageType: string
    lastUpdatedAt: number
}

const AppSocketContext = createContext<AppSocketContextType>({
    isConnected: false,
    sendJson: () => false,
    addMessageListener: () => () => undefined,
})

function getSocketUrl() {
    const configuredSocketUrl = process.env.REACT_APP_WS_BASE_URL?.trim()
    if (configuredSocketUrl) {
        return configuredSocketUrl
    }

    const socketProtocol = window.location.host.startsWith("localhost") ? "ws" : "wss"
    return `${socketProtocol}://${window.location.hostname}${socketProtocol === "ws" ? ":8000" : ""}/ws/app/`
}

function AppSocketProvider(props: { children: React.ReactNode; accessToken: string | null; shouldConnect: boolean }) {
    const socketRef = useRef<WebSocket | null>(null)
    const reconnectTimeoutRef = useRef<number | null>(null)
    const reconnectAttemptsRef = useRef(0)
    const shouldConnectRef = useRef(false)
    const accessTokenRef = useRef<string | null>(null)
    const manuallyClosedRef = useRef(false)
    const listenersRef = useRef(new Set<AppSocketListener>())
    const versionCheckTimerRef = useRef<number | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const [isFrontendOutdated, setIsFrontendOutdated] = useState(false)
    const [debugState, setDebugState] = useState<DebugState>({
        reconnectAttempts: 0,
        lastEvent: "init",
        lastMessageType: "-",
        lastUpdatedAt: Date.now(),
    })
    //const isDev = process.env.NODE_ENV !== "production"
    const isDev = false

    useEffect(() => {
        if (FRONTEND_VERSION === "unknown") {
            console.warn("[app-socket] FRONTEND_VERSION is unknown. Check REACT_APP_FRONTEND_VERSION build-time setup.")
        }
    }, [])

    const updateDebugState = useCallback((partialState: Partial<DebugState>) => {
        setDebugState((currentState) => ({
            ...currentState,
            ...partialState,
            lastUpdatedAt: Date.now(),
        }))
    }, [])

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimeoutRef.current !== null) {
            window.clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
    }, [])

    const clearVersionCheckTimer = useCallback(() => {
        if (versionCheckTimerRef.current !== null) {
            window.clearInterval(versionCheckTimerRef.current)
            versionCheckTimerRef.current = null
        }
    }, [])

    const sendFrontendVersionCheck = useCallback(() => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            return false
        }

        socketRef.current.send(
            JSON.stringify({
                type: "frontend.version.check",
                data: {
                    frontend_version: FRONTEND_VERSION,
                },
            })
        )

        updateDebugState({
            lastEvent: "frontend.version.check.sent",
        })

        return true
    }, [updateDebugState])

    const handleVersionStatusMessage = useCallback((message: AppSocketMessage) => {
        if (!VERSION_STATUS_MESSAGE_TYPES.has(message.msg_type)) {
            return
        }

        const serverPayload = message.data || {}
        const latestVersion = serverPayload.latest_version || serverPayload.current_version || serverPayload.frontend_version
        const outdatedByFlag = serverPayload.outdated === true
        const outdatedByVersionMismatch = typeof latestVersion === "string" && FRONTEND_VERSION !== "unknown" && latestVersion !== FRONTEND_VERSION

        if (outdatedByFlag || outdatedByVersionMismatch) {
            setIsFrontendOutdated(true)
            updateDebugState({
                lastEvent: "frontend.version.outdated",
            })
        }
    }, [updateDebugState])

    const scheduleReconnect = useCallback((connectFn: () => void) => {
        if (!shouldConnectRef.current) {
            return
        }

        clearReconnectTimer()
        const delay = Math.min(10000, 1000 * Math.pow(2, reconnectAttemptsRef.current))
        reconnectAttemptsRef.current += 1
        updateDebugState({
            reconnectAttempts: reconnectAttemptsRef.current,
            lastEvent: `reconnect.scheduled.${delay}ms`,
        })
        reconnectTimeoutRef.current = window.setTimeout(() => {
            connectFn()
        }, delay)
    }, [clearReconnectTimer, updateDebugState])

    const disconnect = useCallback((manuallyClosed = true) => {
        manuallyClosedRef.current = manuallyClosed
        clearReconnectTimer()
        clearVersionCheckTimer()

        if (socketRef.current) {
            socketRef.current.close()
            socketRef.current = null
        }

        setIsConnected(false)
        updateDebugState({
            lastEvent: manuallyClosed ? "disconnect.manual" : "disconnect",
        })
    }, [clearReconnectTimer, clearVersionCheckTimer, updateDebugState])

    const connect = useCallback(() => {
        if (!shouldConnectRef.current || !accessTokenRef.current) {
            return
        }

        if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
            return
        }

        manuallyClosedRef.current = false
        updateDebugState({
            lastEvent: "connect.start",
        })
        const baseSocketUrl = getSocketUrl()
        const separator = baseSocketUrl.includes("?") ? "&" : "?"
        const socketUrlWithToken = `${baseSocketUrl}${separator}token=${encodeURIComponent(accessTokenRef.current)}`
        const ws = new WebSocket(socketUrlWithToken, ["Token", accessTokenRef.current])
        socketRef.current = ws

        ws.onopen = () => {
            reconnectAttemptsRef.current = 0
            setIsConnected(true)
            updateDebugState({
                reconnectAttempts: 0,
                lastEvent: "connect.open",
            })

            sendFrontendVersionCheck()
            clearVersionCheckTimer()
            versionCheckTimerRef.current = window.setInterval(() => {
                sendFrontendVersionCheck()
            }, VERSION_CHECK_INTERVAL_MS)
        }

        ws.onmessage = (event) => {
            try {
                const parsedData = JSON.parse(event.data)
                updateDebugState({
                    lastEvent: "message.received",
                    lastMessageType: parsedData?.msg_type || "unknown",
                })
                handleVersionStatusMessage(parsedData)
                listenersRef.current.forEach((listener) => listener(parsedData))
            } catch {
                // ignore malformed websocket payloads
                updateDebugState({
                    lastEvent: "message.invalid_json",
                })
            }
        }

        ws.onerror = () => {
            updateDebugState({
                lastEvent: "socket.error",
            })
            ws.close()
        }

        ws.onclose = () => {
            socketRef.current = null
            setIsConnected(false)
            clearVersionCheckTimer()
            updateDebugState({
                lastEvent: "connect.close",
            })

            if (!manuallyClosedRef.current && shouldConnectRef.current) {
                scheduleReconnect(connect)
            }
        }
    }, [scheduleReconnect, updateDebugState, sendFrontendVersionCheck, clearVersionCheckTimer, handleVersionStatusMessage])

    useEffect(() => {
        accessTokenRef.current = props.accessToken
        shouldConnectRef.current = Boolean(props.shouldConnect && props.accessToken)

        if (!shouldConnectRef.current) {
            disconnect(true)
            return
        }

        disconnect(true)
        reconnectAttemptsRef.current = 0
        connect()
    }, [props.accessToken, props.shouldConnect, connect, disconnect])

    useEffect(() => {
        return () => {
            clearVersionCheckTimer()
            disconnect(true)
        }
    }, [disconnect, clearVersionCheckTimer])

    const sendJson = useCallback((payload: unknown) => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            updateDebugState({
                lastEvent: "send.skipped.not_open",
            })
            return false
        }

        socketRef.current.send(JSON.stringify(payload))
        updateDebugState({
            lastEvent: "send.ok",
        })
        return true
    }, [updateDebugState])

    const addMessageListener = useCallback((listener: AppSocketListener) => {
        listenersRef.current.add(listener)

        return () => {
            listenersRef.current.delete(listener)
        }
    }, [])

    const contextValue = useMemo(() => {
        return {
            isConnected,
            sendJson,
            addMessageListener,
        }
    }, [isConnected, sendJson, addMessageListener])

    return (
        <AppSocketContext.Provider value={contextValue}>
            {props.children}
            {isFrontendOutdated && (
                <>
                    <div className="modal__overlay" />
                    <div className="modal__card">
                        <FrontendUpdateModal onReload={() => window.location.reload()} />
                    </div>
                </>
            )}
            {isDev && (
                <div
                    style={{
                        position: "fixed",
                        right: "12px",
                        bottom: "12px",
                        zIndex: 99999,
                        background: "rgba(15, 23, 42, 0.92)",
                        color: "#e2e8f0",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        fontSize: "11px",
                        lineHeight: 1.4,
                        minWidth: "220px",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.28)",
                        pointerEvents: "none",
                        fontFamily: "monospace",
                    }}
                >
                    <div>app-socket: {isConnected ? "connected" : "disconnected"}</div>
                    <div>reconnect-attempts: {debugState.reconnectAttempts}</div>
                    <div>last-event: {debugState.lastEvent}</div>
                    <div>last-message-type: {debugState.lastMessageType}</div>
                    <div>updated: {new Date(debugState.lastUpdatedAt).toLocaleTimeString()}</div>
                </div>
            )}
        </AppSocketContext.Provider>
    )
}

function useAppSocket() {
    return useContext(AppSocketContext)
}

export { AppSocketProvider, useAppSocket }
